<template>
  <section
    class="roadmap-modern-wrapper"
    aria-label="Run sheet detail (moderne)"
    role="main"
  >
    <div
      v-if="isLoading"
      class="loading-container d-print-none"
      aria-live="polite"
      aria-busy="true"
    >
      <div class="loading-spinner" />
      <p class="loading-text">
        Chargement de la run sheet...
      </p>
    </div>

    <div
      v-if="roadmap"
      class="roadmap-content-wrapper"
    >
      <!-- Modern Top Action Bar -->
      <div class="top-action-bar mb-6 d-print-none">
        <div class="action-bar-content">
          <div class="title-section">
            <div class="title-icon">
              <feather-icon
                icon="MapIcon"
                size="28"
              />
            </div>
            <div class="title-text">
              <h1 class="page-title">
                Run sheet
              </h1>
              <span class="roadmap-id">#{{ roadmap.id }}</span>
            </div>
          </div>
          <div class="action-buttons">
            <button
              class="action-btn primary-btn"
              @click="printPage"
            >
              <feather-icon
                icon="PrinterIcon"
                size="18"
              />
              <span>Imprimer</span>
            </button>
            <router-link
              :to="{ name: 'roadmap-list' }"
              class="action-btn secondary-btn"
            >
              <feather-icon
                icon="ArrowLeftIcon"
                size="18"
              />
              <span>Retour</span>
            </router-link>
          </div>
        </div>
      </div>

      <!-- Main Content Card -->
      <div
        id="roadmap-content"
        class="roadmap-content"
      >
        <div class="roadmap-card">
          <!-- Screen Header - Modern Design -->
          <div class="screen-header">
            <div class="header-background">
              <div class="gradient-overlay" />
              <div class="pattern-overlay" />
            </div>
            <div class="header-content">
              <div class="header-left">
                <div class="document-badge">
                  <feather-icon
                    icon="MapIcon"
                    size="16"
                  />
                  <span>RUN SHEET</span>
                </div>
                <div class="document-number">
                  #{{ roadmap.id }}
                </div>
                <div class="document-meta">
                  <div class="meta-item">
                    <feather-icon
                      icon="CalendarIcon"
                      size="16"
                    />
                    <div class="meta-content">
                      <span class="meta-label">Créée le</span>
                      <span class="meta-value">{{ formatDate(roadmap.created_at) }}</span>
                    </div>
                  </div>
                  <div class="meta-item">
                    <feather-icon
                      :icon="roadmap.is_validated ? 'CheckCircleIcon' : 'ClockIcon'"
                      size="16"
                    />
                    <div class="meta-content">
                      <span class="meta-label">Statut</span>
                      <span
                        class="status-badge"
                        :class="roadmap.is_validated ? 'validated' : 'pending'"
                      >
                        {{ roadmap.is_validated ? 'Validée' : 'En attente' }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="header-right">
                <div class="company-section">
                  <div class="company-logo">
                    <img
                      src="@/assets/images/logo/yass-express-logo.png"
                      :alt="$branding.logo.alt"
                    >
                  </div>
                  <div class="company-info">
                    <div class="company-name">
                      {{ company.name || $branding.company.shortName }}
                    </div>
                    <div class="company-subtitle">
                      Delivery
                    </div>
                    <div class="company-tagline">
                      {{ company.tagline || 'Solutions logistiques' }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Print Header - Clean B&W Design -->
          <div class="print-header d-none">
            <div class="print-header-content">
              <div class="print-left">
                <div class="print-document-type">
                  RUN SHEET
                </div>
                <div class="print-document-id">
                  #{{ roadmap.id }}
                </div>
                <div class="print-meta">
                  <div class="print-meta-row">
                    <span class="print-label">Créée le:</span>
                    <span class="print-value">{{ formatDate(roadmap.created_at) }}</span>
                  </div>
                  <div class="print-meta-row">
                    <span class="print-label">Statut:</span>
                    <span class="print-status">{{ roadmap.is_validated ? 'Validée' : 'En attente' }}</span>
                  </div>
                </div>
              </div>
              <div class="print-right">
                <div class="print-company">
                  <img
                    src="/logo.png"
                    :alt="$branding.logo.alt"
                    class="print-logo"
                  >
                  <div class="print-company-text">
                    <div class="print-company-name">
                      {{ company.name || $branding.company.shortName }}
                    </div>
                    <div class="print-company-line">
                      Delivery - Solutions logistiques
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="print-separator" />
          </div>

          <!-- Info Grid - Modern Cards -->
          <div class="info-grid">
            <div class="info-card depot-card">
              <div class="card-icon">
                <feather-icon
                  icon="PackageIcon"
                  size="24"
                  class="d-print-none"
                />
              </div>
              <div class="card-content">
                <div class="card-label">
                  Dépôt
                </div>
                <div class="card-value">
                  {{ roadmap.repository && roadmap.repository.name || 'Non défini' }}
                </div>
              </div>
            </div>
            <div class="info-card driver-card">
              <div class="card-icon">
                <feather-icon
                  icon="UserIcon"
                  size="24"
                  class="d-print-none"
                />
              </div>
              <div class="card-content">
                <div class="card-label">
                  Livreur
                </div>
                <div class="card-value">
                  {{ roadmap.delivery_man && roadmap.delivery_man.username || 'Non assigné' }}
                </div>
              </div>
            </div>
            <div class="info-card vehicle-card">
              <div class="card-icon">
                <feather-icon
                  icon="TruckIcon"
                  size="24"
                  class="d-print-none"
                />
              </div>
              <div class="card-content">
                <div class="card-label">
                  Véhicule
                </div>
                <div class="card-value">
                  {{ roadmap.delivery_man && roadmap.delivery_man.car && roadmap.delivery_man.car.serial_number || 'Non défini' }}
                </div>
              </div>
            </div>
          </div>

          <!-- Orders Section -->
          <div class="orders-section">
            <div class="section-header">
              <div class="section-title">
                <feather-icon
                  icon="ListIcon"
                  size="20"
                  class="d-print-none"
                />
                <span>Colis à livrer</span>
                <div class="orders-count">
                  {{ roadmap.orders && roadmap.orders.length || 0 }}
                </div>
              </div>
            </div>

            <div class="table-wrapper">
              <b-table-lite
                :items="roadmap.orders"
                :fields="orderFields"
                class="modern-table"
                table-class="data-table"
                thead-class="table-header"
                tbody-class="table-body"
                small
              >
                <template #cell(supplier)="{ value }">
                  <div class="supplier-cell">
                    <div class="supplier-name">
                      {{ value && value.username || 'N/A' }}
                    </div>
                  </div>
                </template>

                <template #cell(name)="{ value, item }">
                  <div class="recipient-cell">
                    <div class="recipient-name">
                      {{ value }}
                    </div>
                    <div
                      v-if="item.phone1"
                      class="recipient-phone"
                    >
                      {{ item.phone1 }}
                    </div>
                    <div
                      v-if="item.phone2"
                      class="recipient-phone"
                    >
                      {{ item.phone2 }}
                    </div>
                  </div>
                </template>

                <template #cell(address)="{ value, item }">
                  <div class="address-cell">
                    <div class="address-region">
                      <span v-if="item.governorate">{{ item.governorate }}</span>
                      <span v-if="item.governorate && item.delegation">, </span>
                      <span v-if="item.delegation">{{ item.delegation }}</span>
                      <span v-if="(item.governorate || item.delegation) && item.locality">, </span>
                      <span v-if="item.locality">{{ item.locality }}</span>
                    </div>
                    <div
                      v-if="value"
                      class="address-detail"
                    >
                      {{ value }}
                    </div>
                  </div>
                </template>

                <template #cell(price_ttc)="{ value, item }">
                  <div class="price-cell">
                    <div class="price-amount">
                      {{ formatPrice(value) }}
                    </div>
                    <div
                      v-if="hasOrderFlags(item)"
                      class="order-flags"
                    >
                      <span
                        v-if="item.is_an_exchange"
                        class="flag exchange-flag"
                        title="Échange"
                      >
                        <feather-icon
                          icon="RepeatIcon"
                          size="12"
                          class="d-print-none"
                        />
                        <span class="d-none d-print-inline">Échange</span>
                      </span>
                      <span
                        v-if="item.is_fragile"
                        class="flag fragile-flag"
                        title="Fragile"
                      >
                        <feather-icon
                          icon="AlertCircleIcon"
                          size="12"
                          class="d-print-none"
                        />
                        <span class="d-none d-print-inline">Fragile</span>
                      </span>
                      <span
                        v-if="item.return_number && item.return_number > 0"
                        class="flag return-flag"
                        :title="'Retour: ' + item.return_number"
                      >
                        <feather-icon
                          icon="BoxIcon"
                          size="12"
                          class="d-print-none"
                        />
                        <span class="d-print-none">{{ item.return_number }}</span>
                        <span class="d-none d-print-inline">Retour: {{ item.return_number }}</span>
                      </span>
                    </div>
                  </div>
                </template>
              </b-table-lite>
            </div>
          </div>

          <!-- Total Section -->
          <div class="total-section">
            <div class="total-card">
              <div class="total-label">
                Total à encaisser
              </div>
              <div class="total-amount">
                {{ formatPrice(roadmap.total_price) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import {
  BTableLite,
} from 'bootstrap-vue'
import axios from 'axios'
import storeAuth from '@/store/store'

export default {
  name: 'ShowRoadmapModern',
  components: {
    BTableLite,
  },
  data() {
    return {
      roadmap: null,
      isLoading: false,
      company: {},
      orderFields: [
        { key: 'id', label: 'BL', class: 'id-column' },
        { key: 'supplier', label: 'Fournisseur', class: 'supplier-column' },
        { key: 'name', label: 'Destinataire', class: 'recipient-column' },
        { key: 'address', label: 'Adresse', class: 'address-column' },
        { key: 'price_ttc', label: 'Prix (TND)', class: 'price-column' },
      ],
    }
  },
  created() {
    this.company = storeAuth.state.company
    this.fetchRoadmap()
  },
  methods: {
    async fetchRoadmap() {
      this.isLoading = true
      try {
        const { data } = await axios.get(`/api/roadmaps/detail/${this.$route.params.id}`)
        if (data.orders) {
          data.orders = data.orders.map(order => ({
            id: order.display_cross_tenant_barcode && order.cross_tenant_barcode ? order.cross_tenant_barcode : order.id,
            supplier: order.supplier ? {
              username: order.supplier.username,
              phone: order.supplier.phone,
              phone2: order.supplier.phone2,
            } : null,
            name: order.name,
            phone1: order.phone1,
            phone2: order.phone2,
            governorate: order.governorate,
            delegation: order.delegation,
            locality: order.locality,
            address: order.address,
            price_ttc: order.price_ttc,
            is_an_exchange: order.is_an_exchange,
            is_fragile: order.is_fragile,
            return_number: order.return_number,
          }))
        }
        this.roadmap = data
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error fetching roadmap:', e)
        this.roadmap = null
      } finally {
        this.isLoading = false
      }
    },
    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      return d.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    formatPrice(value) {
      if (!value) return '0.000 TND'
      return `${Number(value).toLocaleString('fr-FR', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      })} TND`
    },
    hasOrderFlags(item) {
      return item.is_an_exchange || item.is_fragile || (item.return_number && item.return_number > 0)
    },
    printPage() {
      const printStyles = `
        @media print {
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            height: auto !important;
            min-height: 0 !important;
          }

          @page {
            margin: 0 !important;
            size: A4;
          }

          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          body {
            margin: 0 !important;
            padding: 0 !important;
          }

          body * {
            visibility: hidden;
          }

          .roadmap-modern-wrapper,
          .roadmap-content-wrapper {
            visibility: visible !important;
            min-height: 0 !important;
            height: auto !important;
            padding: 0 !important;
            margin: 0 !important;
          }

          #roadmap-content,
          #roadmap-content * {
            visibility: visible;
          }

          #roadmap-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0 !important;
            padding: 1mm !important;
          }
            
            .d-print-none {
              display: none !important;
            }
            
            .d-none {
              display: block !important;
            }
            
            .d-print-inline {
              display: inline !important;
            }
            
            /* Hide screen header, show print header */
            .screen-header {
              display: none !important;
            }
            
            .print-header {
              display: block !important;
            }
            
            /* Print-specific styles */
            .roadmap-card {
              background: white !important;
              box-shadow: none !important;
              border: none !important;
              padding: 0 !important;
              margin: 0 !important;
            }
            
            .print-header-content {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              padding: 20px 0;
              margin-bottom: 20px;
            }
            
            .print-document-type {
              background: #dc2626 !important;
              color: white !important;
              padding: 8px 16px;
              font-size: 12px;
              font-weight: bold;
              margin-bottom: 12px;
              display: inline-block;
            }
            
            .print-document-id {
              font-size: 32px;
              font-weight: bold;
              color: black;
              margin-bottom: 15px;
            }
            
            .print-meta-row {
              display: flex;
              margin-bottom: 8px;
              font-size: 14px;
            }
            
            .print-label {
              font-weight: bold;
              margin-right: 10px;
              min-width: 80px;
            }
            
            .print-status {
              border: 1px solid black;
              padding: 2px 8px;
              font-size: 12px;
              font-weight: bold;
            }
            
            .print-company {
              display: flex;
              align-items: center;
              border: 2px solid #dc2626;
              padding: 15px;
            }
            
            .print-logo {
              width: 40px;
              height: 40px;
              margin-right: 15px;
            }
            
            .print-company-name {
              font-size: 24px;
              font-weight: bold;
            }
            
            .print-company-line {
              font-size: 14px;
            }
            
            .print-separator {
              height: 3px;
              background: #dc2626;
              margin: 20px 0;
            }
            
            /* Info grid for print */
            .info-grid {
              display: flex;
              margin-bottom: 25px;
            }
            
            .info-card {
              flex: 1;
              border: 1px solid #dc2626 !important;
              background: white !important;
              margin-right: 10px;
              padding: 15px !important;
              box-shadow: none !important;
            }
            
            .info-card:last-child {
              margin-right: 0;
            }
            
            .card-label {
              font-weight: bold !important;
              font-size: 14px !important;
              color: black !important;
              border-bottom: 1px solid #dc2626 !important;
              padding-bottom: 5px !important;
              margin-bottom: 8px !important;
            }
            
            .card-value {
              font-size: 14px !important;
              color: black !important;
            }
            
            /* Table styles */
            .section-title {
              font-size: 18px !important;
              font-weight: bold !important;
              color: black !important;
              border-bottom: 2px solid #dc2626 !important;
              padding-bottom: 8px !important;
              margin-bottom: 15px !important;
            }
            
            .orders-count {
              background: #dc2626 !important;
              color: white !important;
              padding: 4px 8px !important;
              font-size: 12px !important;
              margin-left: 10px !important;
            }
            
            .data-table {
              width: 100% !important;
              border-collapse: collapse !important;
              font-size: 12px !important;
            }
            
            .data-table th {
              background: #dc2626 !important;
              color: white !important;
              padding: 10px 8px !important;
              border: 1px solid #dc2626 !important;
              font-weight: bold !important;
              text-align: left !important;
              display: table-cell !important;
              visibility: visible !important;
            }
            
            .data-table td {
              padding: 8px !important;
              border: 1px solid #dc2626 !important;
              color: black !important;
              background: white !important;
              vertical-align: top !important;
            }
            
            .table-header {
              display: table-header-group !important;
              visibility: visible !important;
            }
            
            .table-header th {
              display: table-cell !important;
              visibility: visible !important;
            }
            
            .supplier-name,
            .recipient-name {
              font-weight: bold !important;
              margin-bottom: 4px !important;
            }
            
            .supplier-phone,
            .recipient-phone,
            .address-region,
            .address-detail {
              font-size: 11px !important;
              color: black !important;
              margin-bottom: 2px !important;
            }
            
            .price-amount {
              font-weight: bold !important;
              margin-bottom: 4px !important;
            }
            
            .order-flags {
              display: flex !important;
              flex-wrap: wrap !important;
              gap: 4px !important;
            }
            
            .flag {
              border: 1px solid black !important;
              padding: 2px 4px !important;
              font-size: 10px !important;
              font-weight: bold !important;
              background: white !important;
              color: black !important;
            }
            
            /* Total section */
            .total-section {
              margin-top: 25px !important;
              text-align: right !important;
            }
            
            .total-card {
              border: 2px solid #dc2626 !important;
              background: white !important;
              padding: 15px 20px !important;
              display: inline-block !important;
              box-shadow: none !important;
            }
            
            .total-label {
              font-size: 14px !important;
              font-weight: bold !important;
              color: black !important;
              margin-bottom: 5px !important;
            }
            
            .total-amount {
              font-size: 20px !important;
              font-weight: bold !important;
              color: black !important;
            }
          }
        </style>
      `

      // Remove existing print styles
      const existingStyles = document.getElementById('dynamic-print-styles')
      if (existingStyles) {
        existingStyles.remove()
      }

      // Add print styles
      const styleElement = document.createElement('style')
      styleElement.id = 'dynamic-print-styles'
      styleElement.textContent = printStyles
      document.head.appendChild(styleElement)

      // Print
      window.print()

      // Clean up
      setTimeout(() => {
        const styles = document.getElementById('dynamic-print-styles')
        if (styles) {
          styles.remove()
        }
      }, 1000)
    },
  },
}
</script>

<style lang="scss" scoped>
// Yass Express branding colors
$brand-red: #dc2626;
$brand-red-light: #ef4444;
$brand-red-dark: #b91c1c;
$brand-black: #1f2937;
$brand-gray: #374151;
$success: #10b981;
$warning: #f59e0b;
$danger: #ef4444;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-800: #1f2937;
$gray-900: #111827;

.roadmap-modern-wrapper {
  min-height: 100vh;
  background: linear-gradient(135deg, $gray-50 0%, #ffffff 100%);
  padding: 2rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

// Loading state
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;

  .loading-spinner {
    width: 48px;
    height: 48px;
    border: 4px solid $gray-200;
    border-top: 4px solid $brand-red;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  .loading-text {
    margin-top: 1rem;
    color: $gray-600;
    font-size: 1.1rem;
    font-weight: 500;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

// Top action bar
.top-action-bar {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid $gray-200;

  .action-bar-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem;
  }

  .title-section {
    display: flex;
    align-items: center;
    gap: 1rem;

    .title-icon {
      width: 56px;
      height: 56px;
      background: linear-gradient(135deg, $brand-red 0%, $brand-red-light 100%);
      border-radius: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      box-shadow: 0 4px 6px -1px rgba(220, 38, 38, 0.3);
    }

    .title-text {
      .page-title {
        font-size: 1.875rem;
        font-weight: 700;
        color: $gray-900;
        margin: 0 0 0.25rem 0;
        line-height: 1.2;
      }

      .roadmap-id {
        color: $gray-600;
        font-size: 1.125rem;
        font-weight: 500;
      }
    }
  }

  .action-buttons {
    display: flex;
    gap: 0.75rem;

    .action-btn {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 1.5rem;
      border-radius: 12px;
      font-weight: 600;
      font-size: 0.875rem;
      transition: all 0.2s ease;
      text-decoration: none;
      border: none;
      cursor: pointer;

      &.primary-btn {
        background: linear-gradient(135deg, $brand-red 0%, $brand-red-dark 100%);
        color: white;
        box-shadow: 0 4px 6px -1px rgba(220, 38, 38, 0.3);

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 12px -1px rgba(220, 38, 38, 0.4);
        }
      }

      &.secondary-btn {
        background: white;
        color: $gray-700;
        border: 2px solid $gray-200;

        &:hover {
          background: $gray-50;
          border-color: $gray-300;
          transform: translateY(-1px);
        }
      }
    }
  }
}

// Main content
.roadmap-content-wrapper {
  animation: fadeIn 0.6s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.roadmap-card {
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
  border: 1px solid $gray-200;
  overflow: hidden;
}

// Screen header - Modern design with Yass Express branding
.screen-header {
  position: relative;
  padding: 0;
  margin-bottom: 2rem;

  .header-background {
    position: absolute;
    inset: 0;

    .gradient-overlay {
      position: absolute;
      inset: 0;
      background: linear-gradient(135deg, $brand-red 0%, $brand-red-light 50%, $brand-red-dark 100%);
    }

    .pattern-overlay {
      position: absolute;
      inset: 0;
      background-image:
        radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.1) 1px, transparent 1px),
        radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.1) 1px, transparent 1px);
      background-size: 30px 30px;
    }
  }

  .header-content {
    position: relative;
    z-index: 10;
    padding: 3rem 2.5rem;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    .header-left {
      flex: 1;

      .document-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        background: rgba(255, 255, 255, 0.2);
        color: white;
        padding: 10px 20px;
        border-radius: 50px;
        font-size: 0.875rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 1.5rem;
        backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.3);
      }

      .document-number {
        font-size: 3.5rem;
        font-weight: 900;
        color: white;
        margin-bottom: 2rem;
        line-height: 1;
        text-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
      }

      .document-meta {
        display: flex;
        flex-direction: column;
        gap: 1rem;

        .meta-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          background: rgba(255, 255, 255, 0.15);
          padding: 1rem 1.25rem;
          border-radius: 12px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);

          .meta-content {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;

            .meta-label {
              color: rgba(255, 255, 255, 0.9);
              font-size: 0.875rem;
              font-weight: 500;
            }

            .meta-value {
              color: white;
              font-weight: 600;
            }

            .status-badge {
              padding: 6px 12px;
              border-radius: 20px;
              font-size: 0.75rem;
              font-weight: 700;
              text-transform: uppercase;
              letter-spacing: 0.5px;

              &.validated {
                background: $success;
                color: white;
              }

              &.pending {
                background: $warning;
                color: white;
              }
            }
          }
        }
      }
    }

    .header-right {
      .company-section {
        background: rgba(255, 255, 255, 0.15);
        border-radius: 16px;
        padding: 2rem;
        backdrop-filter: blur(15px);
        border: 1px solid rgba(255, 255, 255, 0.2);
        display: flex;
        align-items: center;
        gap: 1.25rem;
        min-width: 280px;

        .company-logo {
          img {
            width: 60px;
            height: 60px;
            filter: brightness(0) invert(1);
            opacity: 0.9;
          }
        }

        .company-info {
          .company-name {
            font-size: 1.75rem;
            font-weight: 800;
            color: white;
            line-height: 1.1;
            margin-bottom: 0.25rem;
          }

          .company-subtitle {
            font-size: 1.25rem;
            font-weight: 600;
            color: rgba(255, 255, 255, 0.9);
            margin-bottom: 0.5rem;
          }

          .company-tagline {
            font-size: 0.875rem;
            color: rgba(255, 255, 255, 0.8);
            font-weight: 500;
          }
        }
      }
    }
  }
}

// Print header - Clean B&W design (hidden by default)
.print-header {
  display: none;
}

// Info grid - Modern cards with Yass Express branding
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin: 2rem 2.5rem;

  .info-card {
    background: linear-gradient(135deg, white 0%, $gray-50 100%);
    border: 1px solid $gray-200;
    border-radius: 16px;
    padding: 1.75rem;
    display: flex;
    align-items: center;
    gap: 1.25rem;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: linear-gradient(90deg, $brand-red 0%, $brand-red-light 100%);
    }

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 24px -4px rgba(0, 0, 0, 0.15);
      border-color: $brand-red;
    }

    .card-icon {
      width: 48px;
      height: 48px;
      background: linear-gradient(135deg, $brand-red 0%, $brand-red-light 100%);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      flex-shrink: 0;
    }

    .card-content {
      flex: 1;

      .card-label {
        font-size: 0.875rem;
        font-weight: 600;
        color: $gray-600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 0.5rem;
      }

      .card-value {
        font-size: 1.125rem;
        font-weight: 700;
        color: $gray-900;
        line-height: 1.3;
      }
    }

    &.depot-card .card-icon {
      background: linear-gradient(135deg, $brand-red 0%, $brand-red-light 100%);
    }

    &.driver-card .card-icon {
      background: linear-gradient(135deg, $brand-black 0%, $brand-gray 100%);
    }

    &.vehicle-card .card-icon {
      background: linear-gradient(135deg, $warning 0%, #fbbf24 100%);
    }
  }
}

// Orders section
.orders-section {
  margin: 2rem 2.5rem;

  .section-header {
    margin-bottom: 1.5rem;

    .section-title {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      font-size: 1.5rem;
      font-weight: 700;
      color: $gray-900;
      padding-bottom: 1rem;
      border-bottom: 3px solid $brand-red;
      position: relative;

      &::after {
        content: '';
        position: absolute;
        bottom: -3px;
        left: 0;
        width: 80px;
        height: 3px;
        background: linear-gradient(90deg, $brand-red-dark 0%, $brand-red 100%);
      }

      .orders-count {
        background: linear-gradient(135deg, $brand-red 0%, $brand-red-dark 100%);
        color: white;
        padding: 6px 12px;
        border-radius: 20px;
        font-size: 0.875rem;
        font-weight: 600;
        margin-left: auto;
      }
    }
  }

  .table-wrapper {
    background: white;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    border: 1px solid $gray-200;
  }
}

// Modern table styling with Yass Express branding
.modern-table {
  margin: 0;

  :deep(.table-header) {
    background: linear-gradient(135deg, $brand-red 0%, $brand-red-dark 100%);

    th {
      color: white;
      font-weight: 600;
      font-size: 0.875rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      padding: 1.25rem 1rem;
      border: none;
      vertical-align: middle;

      &:first-child { border-radius: 0; }
      &:last-child { border-radius: 0; }
    }
  }

  :deep(.table-body) {
    tr {
      transition: background-color 0.2s ease;

      &:hover {
        background-color: $gray-50;
      }

      &:last-child td {
        border-bottom: none;
      }
    }

    td {
      padding: 1rem;
      border-bottom: 1px solid $gray-200;
      border-left: none;
      border-right: none;
      vertical-align: top;

      &.id-column {
        font-weight: 700;
        color: $brand-red;
        font-size: 0.875rem;
        width: 80px;
      }

      &.supplier-column,
      &.recipient-column {
        width: 180px;
      }

      &.address-column {
        width: 220px;
      }

      &.price-column {
        width: 140px;
      }
    }
  }
}

// Table cell content
.supplier-cell,
.recipient-cell {
  .supplier-name,
  .recipient-name {
    font-weight: 600;
    color: $gray-900;
    margin-bottom: 0.25rem;
    font-size: 0.875rem;
  }

  .supplier-phone,
  .recipient-phone {
    font-size: 0.75rem;
    color: $gray-600;
    font-weight: 500;
  }
}

.address-cell {
  .address-region {
    font-size: 0.875rem;
    font-weight: 600;
    color: $gray-700;
    margin-bottom: 0.25rem;
  }

  .address-detail {
    font-size: 0.75rem;
    color: $gray-600;
    line-height: 1.4;
  }
}

.price-cell {
  .price-amount {
    font-weight: 700;
    color: $gray-900;
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
  }

  .order-flags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;

    .flag {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      padding: 3px 6px;
      border-radius: 6px;
      font-size: 0.625rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.3px;

      &.exchange-flag {
        background: rgba(59, 130, 246, 0.1);
        color: #1d4ed8;
        border: 1px solid rgba(59, 130, 246, 0.2);
      }

      &.fragile-flag {
        background: rgba(239, 68, 68, 0.1);
        color: #dc2626;
        border: 1px solid rgba(239, 68, 68, 0.2);
      }

      &.return-flag {
        background: rgba(245, 158, 11, 0.1);
        color: #d97706;
        border: 1px solid rgba(245, 158, 11, 0.2);
      }
    }
  }
}

// Total section with Yass Express branding
.total-section {
  margin: 2rem 2.5rem;
  display: flex;
  justify-content: flex-end;

  .total-card {
    background: linear-gradient(135deg, $brand-red 0%, $brand-red-dark 100%);
    border-radius: 16px;
    padding: 1.5rem 2rem;
    box-shadow: 0 8px 16px -4px rgba(220, 38, 38, 0.4);
    border: none;
    min-width: 250px;
    text-align: center;

    .total-label {
      color: rgba(255, 255, 255, 0.9);
      font-size: 0.875rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 0.5rem;
    }

    .total-amount {
      color: white;
      font-size: 1.75rem;
      font-weight: 800;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }
  }
}

// Responsive design
@media (max-width: 768px) {
  .roadmap-modern-wrapper {
    padding: 1rem;
  }

  .top-action-bar .action-bar-content {
    flex-direction: column;
    gap: 1.5rem;
    text-align: center;
  }

  .screen-header .header-content {
    flex-direction: column;
    gap: 2rem;
    text-align: center;
  }

  .info-grid {
    grid-template-columns: 1fr;
    margin: 1.5rem;
  }

  .orders-section,
  .total-section {
    margin: 1.5rem;
  }

  .modern-table :deep(.table-header th),
  .modern-table :deep(.table-body td) {
    padding: 0.75rem 0.5rem;
    font-size: 0.75rem;
  }
}

@media (max-width: 480px) {
  .screen-header .header-content {
    padding: 2rem 1.5rem;
  }

  .document-number {
    font-size: 2.5rem !important;
  }

  .info-card {
    padding: 1.25rem !important;
  }

  .total-card {
    min-width: auto !important;
    width: 100%;
  }
}
</style>
